import React, {useState, useEffect} from 'react';
import {Box, Flex, Textarea, VStack, useToast} from '@chakra-ui/react';
import {TextG12, TextG14, TextG16} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import useAuth from '../../../hooks/useAuth';
import {useUpdateProfile} from '../../../hooks/useUpdateProfile';
import styled from 'styled-components';

const SaveButton = styled.button<{$disabled: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#E02F84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

const MAX_CHARS = 250;

interface EditIntroductionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditIntroductionModal = ({isOpen, onClose}: EditIntroductionModalProps) => {
  const {user} = useAuth();
  const updateProfile = useUpdateProfile();
  const [text, setText] = useState(user?.aboutMe || '');
  const isSaving = updateProfile.isLoading;
  const toast = useToast();

  // Re-sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      setText(user?.aboutMe || '');
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS || val.length < text.length) {
      setText(val);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    const trimmed = text.trim();
    onClose();
    try {
      await updateProfile.mutateAsync({aboutMe: trimmed});
      toast({title: 'Introduction updated!', status: 'success', duration: 2000});
    } catch {
      toast({title: 'Could not update introduction', status: 'error', duration: 3000});
    }
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          My Introduction
        </TextG14>
      }
      modalPadding="16px 20px 24px">
      <VStack spacing={5} align="stretch" minH="350px" justify="space-between">
        <Box>
          {/* Info box */}
          <Box
            bg="#F9F9F9"
            borderRadius="12px"
            p={4}
            mb={5}
            boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08);"
            border="2px solid #E9E9E9">
            <TextG14 mt="0" color="#1A1A1A" lineHeight="20px">
              Share something you nerd out about. It's one of the easiest ways for neighbors to
              start a conversation with you.
            </TextG14>
          </Box>

          {/* Textarea */}
          <Box
            border="1px solid #E5E5E5"
            borderRadius="12px"
            p="14px 16px"
            minH="120px"
            _focusWithin={{borderColor: '#1A1A1A'}}>
            <TextG16 mt="0" fontWeight="500" color="#4D4D4D" mb={1}>
              I could happily nerd out about...
            </TextG16>
            <Textarea
              value={text}
              onChange={handleChange}
              fontFamily="Gordita"
              fontSize="14px"
              lineHeight="20px"
              p={0}
              border="none"
              borderRadius={0}
              resize="none"
              minH="110px"
              _focus={{boxShadow: 'none'}}
              _placeholder={{color: '#C4C4C4'}}
            />
            <Flex justify="flex-end">
              <TextG12 mt="0" color="#C4C4C4">
                {text.length}/{MAX_CHARS}
              </TextG12>
            </Flex>
          </Box>
        </Box>

        <SaveButton $disabled={isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

export default EditIntroductionModal;
